import { injectable } from 'inversify';

// Type inferred from an miphubclient library, identified through debugging.
export interface MipHubConnection {
  initialTimeout: number; // value is 960 s = 16 minutes,
  state?: 'active' | 'expired';
  timeLeft: number;
}

export interface IMipHubClient {
  reset: () => void;
  disconnect: () => void;
}

export type MipHubCallback = (conn: MipHubConnection) => void;

export interface MipHub {
  connect: (func: MipHubCallback, sid: string) => Promise<IMipHubClient>;
}

@injectable()
export class MipService {
  private readonly _mipHub: MipHub;

  private _client?: IMipHubClient;

  private readonly _logoutUrl: string;

  private readonly _sid: string;

  private readonly _operationModeSessionTimeInSec: number;

  private readonly _refreshDelay: number;

  private _moveHandler: <T>(event: T) => void;

  public constructor(
    mipHub: MipHub,
    logoutUrl: string,
    sid: string,
    operationModeSessionTimeInSec: number,
    refreshDelayMs?: number
  ) {
    this._mipHub = mipHub;
    this._logoutUrl = logoutUrl;
    this._sid = sid;
    this._operationModeSessionTimeInSec = operationModeSessionTimeInSec;
    this._refreshDelay = refreshDelayMs ?? 500;

    this._moveHandler = this._refreshSession.bind(this);
  }

  private async _initAsync(): Promise<void> {
    this._client = await this._mipHub.connect((p: MipHubConnection) => {
      // this function is called constantly by miphubclient
      if (p.timeLeft <= 0) {
        this._stop();
      }
    }, this._sid);
    this._start();
    this._client.reset();
  }

  public init(): void {
    void this._initAsync();
  }

  private _refreshSession(): void {
    setTimeout(() => {
      // Reset session
      this._client?.reset();
    }, this._refreshDelay);
  }

  private _start(): void {
    document.addEventListener('mousemove', this._moveHandler);
    document.addEventListener('touchmove', this._moveHandler);
  }

  private _stop(): void {
    // Remove event listener and dispose MIP client
    document.removeEventListener('mousemove', this._moveHandler);
    document.removeEventListener('touchmove', this._moveHandler);

    this._client?.disconnect();
    this._client = undefined;

    // Perform full sign out
    if (this._logoutUrl.length > 0) {
      window.onbeforeunload = null;
      const baseUrl = this._logoutUrl.endsWith('/') ? this._logoutUrl : this._logoutUrl + '/';
      const returnUrl = encodeURIComponent(window.location.href);
      window.location.assign(baseUrl + this._sid + '?returnUrl=' + returnUrl);
    }
  }

  public startOperationMode(onEndOperationMode: () => void): void {
    const startTimeInSec = new Date().getTime() / 1000;
    let deltaTime = 0;

    const refreshInterval = setInterval(() => {
      this._refreshSession();

      deltaTime = new Date().getTime() / 1000 - startTimeInSec;
      if (deltaTime >= this._operationModeSessionTimeInSec) {
        onEndOperationMode();
        clearInterval(refreshInterval);
      }
    }, 1000);
  }
}
